import PropTypes from "prop-types";
import { useEffect, useRef } from "react";

// Default sections
import { DefaultInput } from "@/PayPlans/helpers/DefaultSections";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useReferenceState } from "@/Common/hooks/ReferenceState";

// Layouts
import TileSelectButton from "@/Layouts/components/Buttons/TileSelectButton";

// Custom components
import ProductItem from "./ProductItem";

// MUI
import { Grid, Grow } from "@mui/material";

const newProduct = ({
	title = "",
	useDefaultRate = false,
	boostPVR = false,
	useGrossModifier = false,
	hasPVRDefinition = false,
	hasTotalGross = false,
}) => {
	let product = {
		productName: title,
		tiers: [
			{
				from: DefaultInput(0, "Percent"),
				to: DefaultInput(100, "Percent"),
				commission: DefaultInput(0, "Percent"),
			},
		],
	};

	if (useDefaultRate) {
		product = { ...product, useDefaultRate: false };
	}
	if (boostPVR) {
		product = { ...product, boostPVR: false };
	}
	if (useGrossModifier) {
		product = { ...product, grossModifier: DefaultInput(0, "Dollar"), useGrossProfit: false };
	}
	if (hasPVRDefinition) {
		product = { ...product, minPVR: DefaultInput(0, "Dollar"), minPenetration: DefaultInput(0, "Unit") };
	}
	if (hasTotalGross) {
		product = { ...product, applyTotalGross: false };
	}

	return product;
};

const useAvailableProducts = (products = [], selectedProducts = []) => {
	return products
		.filter((product) => !selectedProducts.some((item) => item.productName === product.label))
		.map((product) => ({ label: product.label }));
};

export default function MetricProducts(props) {
	const {
		products = [],
		listProducts = [],
		defaultTiers = { rows: 1, columns: 1 },
		useDefaultRate = false,
		boostPVR = false,
		useGrossModifier = false,
		hasPVRDefinition = false,
		hasTotalGross = false,
		loadingProducts = false,
		disabled = false,
		readOnly = false,
		onActionUpdate = () => {},
		onActionValidation = () => {},
	} = props;

	// Control to send update actions / References
	const wasUpdated = useRef(false);
	const [isValidProducts, setIsValidProducts, validProductsList, recheckValidProducts] = useReferenceState(true, []);

	const onProductsUpdate = (updatedProducts) => {
		onActionValidation?.(isValidProducts);
		if (isValidProducts && wasUpdated.current) {
			onActionUpdate?.(updatedProducts);
		}
	};

	// States
	const [innerProducts, setInnerProducts] = useCallbackState([], onProductsUpdate);

	// Hooks
	const availableProducts = useAvailableProducts(listProducts, innerProducts);

	const handleAddProduct = (selected) => {
		const updated = [...innerProducts];
		const product = newProduct({ title: selected.label, useDefaultRate, boostPVR, useGrossModifier, hasPVRDefinition, hasTotalGross });
		updated.push(product);
		wasUpdated.current = false;
		setInnerProducts(updated);
	};

	const handleDeleteProduct = (index) => {
		const updated = [...innerProducts];
		updated.splice(index, 1);
		validProductsList[index] = true;
		recheckValidProducts();
		update(updated);
	};

	const handleUpdateProduct = (index, product) => {
		const updated = [...innerProducts];
		updated[index] = product;
		update(updated);
	};

	const update = (updatedProducts = []) => {
		setInnerProducts(updatedProducts);
		wasUpdated.current = true;
	};

	useEffect(() => {
		setInnerProducts(products);
		return () => {
			setInnerProducts([]);
			setIsValidProducts(true);
			wasUpdated.current = false;
		};
	}, [products, setInnerProducts, setIsValidProducts]);

	return (
		<Grid container rowSpacing={1} columnSpacing={1}>
			{innerProducts.map((item, key) => {
				const itemKey = `metric_product_${key}`;
				return (
					<Grow key={itemKey} in timeout={500}>
						<Grid item>
							<ProductItem
								index={key}
								product={item}
								defaultTiers={defaultTiers}
								useDefaultRate={useDefaultRate}
								boostPVR={boostPVR}
								useGrossModifier={useGrossModifier}
								hasPVRDefinition={hasPVRDefinition}
								hasTotalGross={hasTotalGross}
								readOnly={readOnly || disabled}
								onActionDelete={handleDeleteProduct}
								onActionUpdate={handleUpdateProduct}
								onActionValidation={(value) => {
									validProductsList[key] = value;
									recheckValidProducts();
								}}
							/>
						</Grid>
					</Grow>
				);
			})}

			<Grid item>
				<TileSelectButton
					tooltip={"Add Product"}
					data={availableProducts}
					onSelect={handleAddProduct}
					showBy={"label"}
					sortBy={"label"}
					loading={loadingProducts}
					disabled={readOnly || !isValidProducts}
				/>
			</Grid>
		</Grid>
	);
}
MetricProducts.propTypes = {
	products: PropTypes.array,
	listProducts: PropTypes.array,
	defaultTiers: PropTypes.shape({
		rows: PropTypes.number,
		columns: PropTypes.number,
	}),
	useDefaultRate: PropTypes.bool,
	boostPVR: PropTypes.bool,
	useGrossModifier: PropTypes.bool,
	hasPVRDefinition: PropTypes.bool,
	hasTotalGross: PropTypes.bool,
	loadingProducts: PropTypes.bool,
	disabled: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
};
